/**
 * CSV-IMPORT-1 (Oct 2) — bringing a business's clients over from a CSV,
 * for setup's "Bring your people with you" step.
 *
 * Rennan: "CSV is failing in the setup". Two reasons, both fixed here:
 *
 *   1. Setup sent the file to the crm-import edge function as
 *      `?action=run` with `csv_rows`. That function reads `body.op`
 *      (so it answered "Unknown op" to every file), and its CSV adapter
 *      was a stub that ignored the rows anyway. Nothing ever came across.
 *      Now the rows go straight into `clients` through RLS ("Members can
 *      create clients"), the same way Settings → Import has always
 *      worked — no edge function in the way.
 *
 *   2. Setup's import comes BEFORE its plan step, so the business is on
 *      Free there, and Free holds 3 clients (CAPS-2's database trigger,
 *      enforce_client_cap). Any real export would have failed at row 4.
 *      So setup now READS the file and HOLDS the clients on this device
 *      (savePendingImport), and they come across the moment the plan is
 *      live (PendingClientImport, mounted with the app). A plan with a
 *      cap (Starter, 15) takes what fits; the rest wait for an upgrade
 *      and come across on their own then. The cap is never bypassed.
 *
 * Held clients are inserted with `created_at` set to when the file was
 * picked — before setup's first Passport goal — so a "New clients" goal
 * doesn't count a whole back catalogue as new.
 *
 * Nothing is overwritten: a client whose email (or, with no email, whose
 * name) is already in the roster is left as it is.
 */
import { supabase } from '@/integrations/supabase/client';
import { parseCsv } from '@/services/import';

export interface ImportedClient {
  name: string;
  email: string | null;
  phone: string | null;
  brand: string | null;
  notes: string | null;
}

/** A file's worth of clients, read and cleaned. */
export interface ReadClients {
  clients: ImportedClient[];
  /** Data rows in the file. */
  rows: number;
  /** Rows with neither a name nor an email. */
  blank: number;
  /** The same person twice in the file (by email, else by name). */
  duplicates: number;
  /** More rows than one import takes; the first MAX_IMPORT_ROWS were kept. */
  truncated: boolean;
}

/** Plenty for any studio's roster, and well inside a browser's storage. */
export const MAX_IMPORT_ROWS = 5000;
/** The biggest file setup will read. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

/** Problems worth saying to the person, word for word. */
export class ClientImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClientImportError';
  }
}

// ── Reading a file ────────────────────────────────────────────────────

type Field = 'full' | 'first' | 'last' | 'email' | 'phone' | 'brand' | 'notes';

/** Header names, lower-cased with everything but letters and digits removed. */
const RULES: { field: Field; exact: string[]; contains?: string[] }[] = [
  {
    field: 'email',
    exact: ['email', 'emailaddress', 'email1', 'primaryemail', 'emailaddress1', 'clientemail', 'contactemail', 'mail'],
    contains: ['email'],
  },
  {
    field: 'first',
    exact: ['firstname', 'first', 'givenname', 'fname', 'clientfirstname', 'contactfirstname', 'primaryfirstname', 'partner1firstname'],
  },
  {
    field: 'last',
    exact: ['lastname', 'last', 'surname', 'familyname', 'lname', 'clientlastname', 'contactlastname', 'primarylastname', 'partner1lastname'],
  },
  {
    field: 'full',
    exact: [
      'name', 'fullname', 'clientname', 'contactname', 'customername', 'displayname', 'client', 'contact',
      'primarycontact', 'primarycontactname', 'clientfullname', 'contactfullname', 'partner1name', 'partner1',
      'couple', 'couplename', 'clientsname',
    ],
  },
  {
    field: 'phone',
    exact: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'mobilenumber', 'cell', 'cellphone', 'telephone', 'tel', 'primaryphone', 'clientphone', 'contactphone'],
    contains: ['phone', 'mobile'],
  },
  {
    field: 'brand',
    exact: ['company', 'companyname', 'business', 'businessname', 'organization', 'organisation', 'brand', 'brandname'],
  },
  { field: 'notes', exact: ['notes', 'note', 'comments', 'comment', 'description'] },
];

/** Words that make a "...name" column something other than a person's name. */
const NOT_A_PERSON = ['company', 'business', 'brand', 'user', 'file', 'venue', 'event', 'project', 'package', 'location', 'job', 'session', 'product', 'organization', 'organisation'];

const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Which column feeds which field (column index, or -1). */
export function mapClientColumns(headers: string[]): Record<Field, number> {
  const keys = headers.map(norm);
  const used = new Set<number>();
  const out = { full: -1, first: -1, last: -1, email: -1, phone: -1, brand: -1, notes: -1 } as Record<Field, number>;
  const take = (field: Field, i: number) => {
    out[field] = i;
    used.add(i);
  };
  for (const r of RULES) {
    const i = keys.findIndex((k, n) => !used.has(n) && r.exact.includes(k));
    if (i !== -1) take(r.field, i);
  }
  for (const r of RULES) {
    if (out[r.field] !== -1 || !r.contains) continue;
    const i = keys.findIndex((k, n) => !used.has(n) && r.contains!.some((c) => k.includes(c)));
    if (i !== -1) take(r.field, i);
  }
  // Last resort for the name: any "…name" column that isn't a company,
  // a venue, a file… ("Client Name (Primary)", "Bride's Name").
  if (out.full === -1 && out.first === -1 && out.last === -1) {
    const i = keys.findIndex((k, n) => !used.has(n) && k.includes('name') && !NOT_A_PERSON.some((w) => k.includes(w)));
    if (i !== -1) take('full', i);
  }
  return out;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const clip = (s: string, n: number) => (s.length > n ? s.slice(0, n) : s);

/** Read a CSV export into clients. Throws ClientImportError with words for the person. */
export function readClientsCsv(text: string): ReadClients {
  const { headers, rows } = parseCsv(text);
  if (!headers.length || !rows.length) {
    throw new ClientImportError("That file didn't have any rows under its header.");
  }
  const col = mapClientColumns(headers);
  if (col.full === -1 && col.first === -1 && col.last === -1 && col.email === -1) {
    throw new ClientImportError(
      "We couldn't find a name or email column in that file. Export your contacts again, or add a header row with Name and Email.",
    );
  }
  const cell = (r: string[], i: number) => (i === -1 ? '' : (r[i] ?? '').trim());
  const seen = new Set<string>();
  const clients: ImportedClient[] = [];
  let blank = 0;
  let duplicates = 0;
  for (const r of rows) {
    const rawEmail = cell(r, col.email);
    const email = EMAIL.test(rawEmail) ? rawEmail : null;
    const full = cell(r, col.full);
    const name = clip(full || [cell(r, col.first), cell(r, col.last)].filter(Boolean).join(' ') || email || '', 200);
    if (!name) {
      blank += 1;
      continue;
    }
    const key = email ? email.toLowerCase() : `name:${name.toLowerCase()}`;
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    clients.push({
      name,
      email,
      phone: clip(cell(r, col.phone), 50) || null,
      brand: clip(cell(r, col.brand), 200) || null,
      notes: clip(cell(r, col.notes), 2000) || null,
    });
  }
  if (!clients.length) {
    throw new ClientImportError("We couldn't find anyone in that file — every row was missing a name and an email.");
  }
  return {
    clients: clients.slice(0, MAX_IMPORT_ROWS),
    rows: rows.length,
    blank,
    duplicates,
    truncated: clients.length > MAX_IMPORT_ROWS,
  };
}

// ── Bringing them in ──────────────────────────────────────────────────

export interface ImportOutcome {
  /** Rows written. */
  imported: number;
  /** Already in the roster (same email, or same name with no email) — left alone. */
  alreadyThere: number;
  /** Didn't fit the plan's client limit — still to come. */
  leftover: ImportedClient[];
  /** The plan's client limit, or null when there isn't one. */
  cap: number | null;
  /** The plan as the server sees it (free, orbit_starter, orbit, orbit_business). */
  plan: string | null;
}

const CHUNK = 100;
const UNLIMITED = 1_000_000_000;

/**
 * Write clients into the business's roster through RLS, skipping anyone
 * already there and stopping at the plan's client limit (the database
 * enforces it either way). `createdAt` back-dates the rows (held imports
 * use the moment the file was picked).
 */
export async function importClients(
  orgId: string,
  clients: ImportedClient[],
  opts: { createdAt?: string } = {},
): Promise<ImportOutcome> {
  // How much room the plan has. If the check itself fails, carry on and
  // let the database's trigger be the judge.
  let cap: number | null = null;
  let current = 0;
  let plan: string | null = null;
  try {
    const { data } = await supabase.rpc('can_add_client', { p_org_id: orgId });
    const g = (data ?? {}) as { current?: number; cap?: number; plan?: string };
    if (typeof g.cap === 'number' && g.cap < UNLIMITED) cap = g.cap;
    current = typeof g.current === 'number' ? g.current : 0;
    plan = g.plan ?? null;
  } catch {
    /* fall through: the trigger still holds the line */
  }

  // Who's already in the roster.
  const have = new Set<string>();
  for (let from = 0; from < 100_000; from += 1000) {
    const { data, error } = await supabase
      .from('clients')
      .select('name, email')
      .eq('org_id', orgId)
      .range(from, from + 999);
    if (error) throw new ClientImportError("We couldn't check who's already in your roster. Try again in a moment.");
    for (const c of (data ?? []) as { name: string | null; email: string | null }[]) {
      if (c.email) have.add(c.email.trim().toLowerCase());
      if (c.name) have.add(`name:${c.name.trim().toLowerCase()}`);
    }
    if ((data ?? []).length < 1000) break;
  }
  const fresh = clients.filter((c) => !have.has(c.email ? c.email.toLowerCase() : `name:${c.name.toLowerCase()}`));
  const alreadyThere = clients.length - fresh.length;

  const room = cap === null ? fresh.length : Math.max(0, cap - current);
  const now = fresh.slice(0, room);
  const leftover = fresh.slice(now.length);

  let imported = 0;
  for (let i = 0; i < now.length; i += CHUNK) {
    const chunk = now.slice(i, i + CHUNK);
    const { error } = await supabase.from('clients').insert(
      chunk.map((c) => ({
        org_id: orgId,
        name: c.name,
        email: c.email,
        phone: c.phone,
        brand: c.brand,
        notes: c.notes,
        ...(opts.createdAt ? { created_at: opts.createdAt } : {}),
      })),
    );
    if (error) {
      if (/client_cap_reached/i.test(error.message)) {
        // Someone added a client while this ran: whatever didn't fit waits.
        leftover.unshift(...now.slice(i));
        break;
      }
      throw new ClientImportError(
        imported
          ? `${imported} came across, then the rest didn't: ${error.message}`
          : `Your clients didn't come across: ${error.message}`,
      );
    }
    imported += chunk.length;
  }

  return { imported, alreadyThere, leftover, cap, plan };
}

// ── Held until the plan starts ────────────────────────────────────────

export interface PendingImport {
  v: 1;
  /** When the file was picked — the held clients are dated then. */
  pickedAt: string;
  fileName: string;
  /** Clients in the file, before anything came across. */
  total: number;
  clients: ImportedClient[];
  /** Told them once that the rest is waiting on an upgrade. */
  toldAboutCap?: boolean;
}

const KEY = (userId: string) => `orbit:pending-client-import:${userId}`;
/** A held file this old is let go — the person can import from Settings. */
const KEEP_MS = 30 * 24 * 60 * 60 * 1000;

export function loadPendingImport(userId: string): PendingImport | null {
  try {
    const raw = window.localStorage.getItem(KEY(userId));
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingImport;
    if (p?.v !== 1 || !Array.isArray(p.clients) || !p.clients.length) return null;
    if (Date.now() - Date.parse(p.pickedAt) > KEEP_MS) {
      window.localStorage.removeItem(KEY(userId));
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

/** False when this browser won't keep it (private mode, full storage). */
export function savePendingImport(userId: string, p: PendingImport): boolean {
  try {
    window.localStorage.setItem(KEY(userId), JSON.stringify(p));
    return true;
  } catch {
    return false;
  }
}

export function clearPendingImport(userId: string): void {
  try {
    window.localStorage.removeItem(KEY(userId));
  } catch {
    /* nothing to clear */
  }
}
